import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import MapPicker from '../../components/MapPicker';
import LibraryPlayerPicker from '../../components/LibraryPlayerPicker';

const EMPTY_PLAYER = {
	name: '',
	country: '',
	pictureUrl: '',
	bracketPicture: '',
	teamTag: '',
	teamPictureUrl: '',
	startggName: '',
	liquipediaName: '',
};

const EMPTY_CONFIG = {
	tournament: {
		title: '',
		aoe4world_tournament_id: null,
		organizers: [],
		sponsors: [],
		statsDate: '',
	},
	settings: {
		lockedMaps: false,
		placeholderPlayerPictureUrl: '',
	},
	players: [],
	maps: [],
	schedule: {},
};

const PLAYER_FIELDS = [
	['name', 'Name'],
	['country', 'Country (2-letter code)'],
	['pictureUrl', 'Picture URL'],
	['bracketPicture', 'Bracket Picture URL'],
	['teamTag', 'Team Tag'],
	['teamPictureUrl', 'Team Picture URL'],
	['startggName', 'Start.GG Name'],
	['liquipediaName', 'Liquipedia Name'],
];

function ConfigEditorDashboard() {
	const { socket, config: envConfig } = useOutletContext();

	const [tournamentList, setTournamentList] = useState([]);
	const [selectedKey, setSelectedKey] = useState('');
	const [newKey, setNewKey] = useState('');
	const [isCreatingNew, setIsCreatingNew] = useState(false);
	const [config, setConfig] = useState(null);
	const [saveStatus, setSaveStatus] = useState(null);
	const [saveError, setSaveError] = useState('');
	const [expandedPlayer, setExpandedPlayer] = useState(null);
	const [allMaps, setAllMaps] = useState([]);
	const [organizersRaw, setOrganizersRaw] = useState('');
	const [sponsorsRaw, setSponsorsRaw] = useState('');
	const [globalPlayers, setGlobalPlayers] = useState([]);
	const [reloadStatus, setReloadStatus] = useState(null);

	useEffect(() => {
		const host = process.env.NODE_ENV === 'production' ? window.location.host : `${window.location.hostname}:3001`;
		fetch(`${window.location.protocol}//${host}/api/config/maps`)
			.then(r => r.json())
			.then(data => setAllMaps(data))
			.catch(() => {});
	}, []);

	useEffect(() => {
		socket.emit('getTournamentList');
		socket.emit('getGlobalPlayers');

		function handleTournamentList(list) {
			setTournamentList(list);
			const defaultKey = envConfig?.tournament_key;
			if (defaultKey && list.includes(defaultKey)) {
				setSelectedKey(defaultKey);
				socket.emit('getRawTournamentConfig', defaultKey);
			}
		}

		function handleRawConfig({ key, config: rawConfig, error }) {
			if (error) {
				console.error('Error loading config:', error);
			} else {
				setConfig(rawConfig);
				setExpandedPlayer(null);
				setOrganizersRaw((rawConfig.tournament?.organizers || []).join(', '));
				setSponsorsRaw((rawConfig.tournament?.sponsors || []).join(', '));
			}
		}

		function handleSaveResult({ success, error }) {
			if (success) {
				setSaveStatus('success');
				setTimeout(() => setSaveStatus(null), 3000);
			} else {
				setSaveStatus('error');
				setSaveError(error || 'Unknown error');
			}
		}

		function handleGlobalPlayers(players) {
			setGlobalPlayers(players);
		}

		socket.on('tournamentList', handleTournamentList);
		socket.on('rawTournamentConfig', handleRawConfig);
		socket.on('saveTournamentConfigResult', handleSaveResult);
		socket.on('globalPlayers', handleGlobalPlayers);

		return () => {
			socket.off('tournamentList', handleTournamentList);
			socket.off('rawTournamentConfig', handleRawConfig);
			socket.off('saveTournamentConfigResult', handleSaveResult);
			socket.off('globalPlayers', handleGlobalPlayers);
		};
	}, [socket, envConfig?.tournament_key]);

	function handleSelectTournament(key) {
		setSelectedKey(key);
		setIsCreatingNew(false);
		setConfig(null);
		if (key) {
			socket.emit('getRawTournamentConfig', key);
		}
	}

	function handleCreateNew() {
		setIsCreatingNew(true);
		setSelectedKey('');
		setNewKey('');
		setConfig(JSON.parse(JSON.stringify(EMPTY_CONFIG)));
		setExpandedPlayer(null);
		setOrganizersRaw('');
		setSponsorsRaw('');
	}

	function handleSave() {
		const key = isCreatingNew ? newKey : selectedKey;
		if (!key) return;
		setSaveStatus('saving');
		socket.emit('saveTournamentConfig', { key, config });
		if (isCreatingNew) {
			setSelectedKey(key);
			setIsCreatingNew(false);
			setNewKey('');
			setTournamentList(prev => [...prev, key].sort());
		}
	}

	function updateTournamentField(field, value) {
		setConfig(prev => ({ ...prev, tournament: { ...prev.tournament, [field]: value } }));
	}

	function updateSettingsField(field, value) {
		setConfig(prev => ({ ...prev, settings: { ...prev.settings, [field]: value } }));
	}

	function updatePlayer(index, field, value) {
		setConfig(prev => {
			const players = [...prev.players];
			players[index] = { ...players[index], [field]: value };
			return { ...prev, players };
		});
	}

	function addPlayer() {
		setConfig(prev => {
			const players = [...prev.players, { ...EMPTY_PLAYER }];
			setExpandedPlayer(players.length - 1);
			return { ...prev, players };
		});
	}

	function addPlayerFromLibrary(libraryPlayer) {
		// Map library Player fields to TournamentPlayer fields
		const newPlayer = {
			name: libraryPlayer.name || '',
			country: libraryPlayer.country || '',
			pictureUrl: libraryPlayer.pictureUrl || '',
			bracketPicture: libraryPlayer.bracketPicture || '',
			teamTag: libraryPlayer.teamTag || '',
			teamPictureUrl: libraryPlayer.teamPictureUrl || '',
			startggName: libraryPlayer.startggName || '',
			liquipediaName: libraryPlayer.liquipediaName || '',
		};
		setConfig(prev => {
			const players = [...prev.players, newPlayer];
			setExpandedPlayer(players.length - 1);
			return { ...prev, players };
		});
	}

	function removePlayer(index) {
		setConfig(prev => ({ ...prev, players: prev.players.filter((_, i) => i !== index) }));
		setExpandedPlayer(prev => (prev === index ? null : prev > index ? prev - 1 : prev));
	}

	function savePlayerToLibrary(index) {
		const player = config.players[index];
		const { teamPictureUrl, bracketPicture, ...libraryFields } = player;
		const existing = globalPlayers.findIndex(p => p.name === player.name);
		let updatedLibrary;
		if (existing >= 0) {
			updatedLibrary = globalPlayers.map((p, i) => i === existing ? { ...p, ...libraryFields } : p);
		} else {
			updatedLibrary = [...globalPlayers, libraryFields];
		}
		socket.emit('saveGlobalPlayers', updatedLibrary);
		setGlobalPlayers(updatedLibrary);
	}

	function addMap(name) {
		setConfig(prev => ({ ...prev, maps: [...prev.maps, name] }));
	}

	function removeMap(index) {
		setConfig(prev => ({ ...prev, maps: prev.maps.filter((_, i) => i !== index) }));
	}

	async function handleReloadStore() {
		setReloadStatus('reloading');
		try {
			const host = process.env.NODE_ENV === 'production' ? window.location.host : `${window.location.hostname}:3001`;
			const res = await fetch(`${window.location.protocol}//${host}/api/store/reload`, { method: 'POST' });
			if (res.ok) {
				setReloadStatus('success');
				socket.emit('getGlobalPlayers');
				setTimeout(() => setReloadStatus(null), 3000);
			} else {
				setReloadStatus('error');
			}
		} catch {
			setReloadStatus('error');
		}
	}

	const activeKey = isCreatingNew ? newKey : selectedKey;

	return (
		<div className='flex flex-col gap-4'>

			{/* Tournament Selector + Reload Store */}
			<div className='flex flex-row gap-2 items-center flex-wrap'>
				<label className='font-bold'>Tournament:</label>
				<select
					value={selectedKey}
					onChange={(e) => handleSelectTournament(e.target.value)}
					disabled={isCreatingNew}
				>
					<option value=''>-- Select --</option>
					{tournamentList.map(key => <option key={key} value={key}>{key}</option>)}
				</select>
				<button onClick={handleCreateNew}>+ New Tournament</button>
				<div className='ml-auto flex items-center gap-2'>
					<button
						className={reloadStatus === 'reloading' ? 'bg-gray-500' : 'bg-blue-700 hover:bg-blue-800'}
						onClick={handleReloadStore}
						disabled={reloadStatus === 'reloading'}
					>
						{reloadStatus === 'reloading' ? 'Reloading…' : 'Reload Store'}
					</button>
					{reloadStatus === 'success' && <span className='text-green-600 text-sm font-medium'>Reloaded!</span>}
					{reloadStatus === 'error' && <span className='text-red-600 text-sm font-medium'>Reload failed</span>}
				</div>
			</div>

			{isCreatingNew && (
				<div className='flex flex-row gap-2 items-center'>
					<label>Tournament Key:</label>
					<input
						type='text'
						value={newKey}
						onChange={(e) => setNewKey(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
						placeholder='my-tournament-name'
						className='w-64'
					/>
					<span className='text-xs text-gray-500'>lowercase letters, numbers, hyphens only</span>
				</div>
			)}

			{config && (
				<>
					<div className='grid grid-cols-2 gap-10'>

						{/* Tournament Info — top left */}
						<section>
							<h2 className='font-bold text-lg border-b mb-2'>Tournament Info</h2>
							<div className='grid grid-cols-[160px_1fr] gap-x-3 gap-y-2 items-center'>
								<label>Title</label>
								<input type='text' value={config.tournament?.title || ''} onChange={(e) => updateTournamentField('title', e.target.value)} className='w-full' />

								<label>AoE4World Tournament ID</label>
								<input type='number' value={config.tournament?.aoe4world_tournament_id ?? ''} onChange={(e) => updateTournamentField('aoe4world_tournament_id', e.target.value ? Number(e.target.value) : null)} className='w-full' />

								<label>Stats Date</label>
								<input type='date' value={config.tournament?.statsDate || ''} onChange={(e) => updateTournamentField('statsDate', e.target.value)} className='w-full' />

								<label>Organizers <span className='text-xs text-gray-500'>(csv)</span></label>
								<input type='text' value={organizersRaw} onChange={(e) => { setOrganizersRaw(e.target.value); updateTournamentField('organizers', e.target.value.split(',').map(s => s.trim()).filter(Boolean)); }} placeholder='EGCTV, OtherOrg' className='w-full' />

								<label>Sponsors <span className='text-xs text-gray-500'>(csv)</span></label>
								<input type='text' value={sponsorsRaw} onChange={(e) => { setSponsorsRaw(e.target.value); updateTournamentField('sponsors', e.target.value.split(',').map(s => s.trim()).filter(Boolean)); }} placeholder="World's Edge, Redbull" className='w-full' />
							</div>
						</section>

						{/* Settings — top right */}
						<section>
							<h2 className='font-bold text-lg border-b mb-2'>Settings</h2>
							<div className='grid grid-cols-[160px_1fr] gap-x-3 gap-y-2 items-center'>
								<label>Locked Maps</label>
								<label className='inline-flex items-center cursor-pointer'>
									<input type='checkbox' checked={config.settings?.lockedMaps || false} className='sr-only peer' onChange={(e) => updateSettingsField('lockedMaps', e.target.checked)} />
									<div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600 dark:peer-checked:bg-blue-600"></div>
								</label>

								<label>Placeholder Player Picture</label>
								<input type='text' value={config.settings?.placeholderPlayerPictureUrl || ''} onChange={(e) => updateSettingsField('placeholderPlayerPictureUrl', e.target.value)} className='w-full' />
							</div>
						</section>

						{/* Players — bottom left */}
						<section>
							<h2 className='font-bold text-lg border-b mb-2'>Players ({config.players?.length || 0})</h2>
							<div className='flex flex-col gap-1'>
								{config.players?.map((player, index) => (
									<div key={index} className='border rounded p-2'>
										<div className='flex flex-row gap-2 items-center cursor-pointer' onClick={() => setExpandedPlayer(expandedPlayer === index ? null : index)}>
											<span className='text-sm w-4 select-none'>{expandedPlayer === index ? '▾' : '▸'}</span>
											<span className='font-medium flex-1'>{player.name || <em className='text-gray-400'>unnamed</em>}</span>
											<img className='h-6' src={`https://flagpedia.net/data/flags/emoji/google/160x160/${player.country}.png`} />
											<button
												className='bg-red-600 hover:bg-red-800 text-xs px-2 py-0.5 h-6'
												onClick={(e) => { e.stopPropagation(); removePlayer(index); }}
											>
												Remove
											</button>
										</div>
										{expandedPlayer === index && (
											<div className='grid grid-cols-[140px_1fr] gap-x-3 gap-y-2 items-center mt-2 ml-6'>
												{PLAYER_FIELDS.map(([field, label]) => (
													<React.Fragment key={field}>
														<label className='text-sm'>{label}</label>
														<input type='text' value={player[field] || ''} onChange={(e) => updatePlayer(index, field, e.target.value)} className='w-full' />
													</React.Fragment>
												))}
												<div className='col-span-2 flex justify-end mt-1'>
													<button
														className='bg-indigo-600 hover:bg-indigo-700 text-xs px-3 py-1'
														onClick={() => savePlayerToLibrary(index)}
													>
														Save to Library
													</button>
												</div>
											</div>
										)}
									</div>
								))}
								<div className='flex flex-row gap-2 mt-2 items-center flex-wrap'>
									<button className='w-32' onClick={addPlayer}>+ Add Player</button>
									<LibraryPlayerPicker players={globalPlayers} onAdd={addPlayerFromLibrary} />
								</div>
							</div>
						</section>

						{/* Maps — bottom right */}
						<section>
							<h2 className='font-bold text-lg border-b mb-2'>Maps ({config.maps?.length || 0})</h2>
							<div className='flex flex-col gap-1'>
								{config.maps?.map((map, index) => (
									<div key={index} className='flex flex-row gap-2 items-center'>
										<span className='flex-1'>{map}</span>
										<button className='bg-red-600 hover:bg-red-800 text-xs px-2 py-0.5 h-6' onClick={() => removeMap(index)}>Remove</button>
									</div>
								))}
								<div className='mt-2'>
									<MapPicker maps={allMaps} onAdd={addMap} />
								</div>
							</div>
						</section>

					</div>

					{/* Save — full width below the grid */}
					<div className='flex flex-row gap-4 items-center pb-8'>
						<button
							className={saveStatus === 'saving' ? 'bg-gray-500' : 'bg-green-700 hover:bg-green-800'}
							onClick={handleSave}
							disabled={saveStatus === 'saving' || !activeKey}
						>
							{saveStatus === 'saving' ? 'Saving...' : 'Save Config'}
						</button>
						{saveStatus === 'success' && <span className='text-green-600 font-medium'>Saved successfully!</span>}
						{saveStatus === 'error' && <span className='text-red-600 font-medium'>Error: {saveError}</span>}
					</div>
				</>
			)}
		</div>
	);
}

export default ConfigEditorDashboard;
